/**
 * The Dayvise client (prompt.md §47, §75).
 *
 * WHY one factory with namespaced methods: web, Tauri desktop and (via the
 * Kotlin port of this same protocol) Android must speak to the *identical*
 * routes. Keeping the route table in one typed object means a backend change
 * shows up as a compile error in every client, and it gives the UI a single
 * place to look for "what can I actually ask Dayvise to do?".
 *
 * Every method maps 1:1 onto a route in `packages/control-plane/src/api.ts`;
 * nothing is invented here.
 */

import type {
  Agent,
  AgentId,
  Approval,
  Conversation,
  ConversationId,
  Credential,
  McpAuthType,
  McpServer,
  McpToolDescriptor,
  McpTransport,
  Membership,
  MembershipRole,
  MemoryRecord,
  Message,
  Run,
  RunId,
  Schedule,
  ScheduleId,
  Skill,
  SkillVisibility,
  Timestamp,
  WorkspaceId,
} from "@dayvise/domain";
import type { ResumeResponse, RunStreamEvent } from "@dayvise/event-protocol";
import type { ClientRunManifest } from "@dayvise/runtime-protocol";
import { SessionResponseSchema, type SessionResponse, type TranscriptFormat } from "@dayvise/contracts";

import { createHttpTransport, type FetchLike, type HttpObservation, type HttpRequestOptions, type HttpTransport } from "./http.ts";
import { normalizeRunEvent } from "./events.ts";
import type { RetryPolicy } from "./retry.ts";
import {
  type LearningEvent,
  type WorkspaceSnapshotView,
  type SnapshotRestoreResult,
  type AutomationTemplateView,
  type AutomationTemplateParameterView,
  type TemplateUseResult,
  type SkillBundleView,
  type BundleInstallPreview,
  type OutputRestorePreview,
  type OutputRestoreResult,
  type ApprovalList,
  type AuditEntry,
  type ClarificationAnswerOutcome,
  type ConnectionTestResult,
  type ConversationDetail,
  type CredentialList,
  type CuratorOverview,
  type SkillVersionRecord,
  type SkillShareView,
  type SkillShareTargetView,
  type LearningRefinement,
  type LearningRefinementTargetKind,
  type EntitlementsSummary,
  type DownloadContent,
  type FileContent,
  type FileSummary,
  type MemoryList,
  type NotificationRecord,
  type RunDetail,
  type SendMessageOutcome,
  type SubagentDelegationSummary,
  type UsageRollup,
  normalizeAuditEntry,
  normalizeConnectionTest,
  normalizeFileSummary,
  normalizeNotification,
  normalizeRunCheckpoint,
  normalizeRunTransition,
  normalizeSendMessage,
  normalizeToolCall,
} from "./types.ts";

/** Query/body input for starting a conversation. */
export interface CreateConversationInput {
  readonly agentId: AgentId | string;
  readonly title?: string;
  readonly visibility?: "private" | "workspace";
}

export interface CreateConversationResponse {
  readonly conversation: Conversation;
}

/** Result of an interactive conversation delete (prompt.md §8.4). */
/** Result of an interactive conversation delete (prompt.md §8.4). */
export interface AdminToolPolicy {
  readonly policyId: string;
  readonly subjectType: string;
  readonly subjectId: string | null;
  readonly toolId: string;
  readonly effect: "allow" | "deny" | "require_approval";
  readonly riskCeiling: string | null;
}

export interface WorkspaceToolCatalogue {
  readonly canManage: boolean;
  readonly permittedMcpTools: readonly string[];
  readonly tools: readonly {
    readonly toolId: string;
    readonly title: string;
    readonly description: string;
    readonly riskLevel: string;
    readonly declared: boolean;
    readonly installed: boolean;
    readonly implementation: "platform" | "runtime" | null;
    readonly executable: boolean;
    readonly availabilityReason: string | null;
    readonly granted: boolean;
    readonly enabled: boolean;
    readonly approvalRequired: boolean;
    readonly policyEffect: AdminToolPolicy["effect"] | null;
  }[];
  readonly mcpServers: readonly {
    readonly mcpServerId: string;
    readonly name: string;
    readonly transport: "http" | "stdio";
    readonly registered: boolean;
    readonly enabled: boolean;
    readonly discovered: boolean;
    readonly toolCount: number;
    readonly health: "unknown" | "healthy" | "unreachable" | "unauthorized";
    readonly grantedToolCount: number;
  }[];
}

export interface DeleteConversationResult {
  readonly deleted: boolean;
  readonly conversationId: string;
  readonly relatedMemories: "keep" | "delete";
  readonly messages: number;
  readonly branches: number;
  readonly files: number;
  readonly memories: number;
}

export interface MessageListResponse {
  readonly messages: readonly Message[];
}

export interface SendMessageInput {
  readonly workingDirectory?: string;
  readonly text: string;
  /** False sends literal prose without command or skill expansion. */
  readonly interpretSlash?: boolean;
  /** Defaults server-side to the conversation's default branch. */
  readonly branchId?: string;
  readonly model?: { readonly provider: string; readonly model: string; readonly reasoningEffort?: import("@dayvise/domain").ReasoningEffort };
  /**
   * Retrying a send with the same key can never start a second run: the server
   * deduplicates on it (prompt.md §15).
   */
  readonly idempotencyKey?: string;
  /**
   * Sealed message secrets (secure text input, e.g. passwords). Values are
   * sealed server-side into the message part and never sent to any model;
   * the agent references them via `{{secret:<id>}}` handles in tool arguments.
   */
  readonly secrets?: readonly { readonly label: string; readonly value: string; readonly reuse?: boolean }[];
  /** Provider processing tier for this message; validated against the resolved provider. */
  readonly processingMode?: "default" | "flex" | "priority";
  readonly attachments?: readonly { readonly fileId: string; readonly version: number; readonly name?: string }[];
}

export interface YoloModeState {
  readonly userEnabled: boolean;
  readonly workspaceEnabled: boolean;
  readonly effective: boolean;
  readonly canManageWorkspace: boolean;
  readonly grants: "none";
}

export interface BoardRecord {
  readonly workspaceId: string;
  readonly boardId: string;
  readonly ownerUserId: string;
  readonly name: string;
  readonly description: string | null;
  readonly visibility: "private" | "workspace";
  readonly conversationId: string | null;
  readonly archivedAt: string | null;
  readonly revision: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface BoardColumn {
  readonly columnId: string;
  readonly boardId: string;
  readonly name: string;
  readonly position: number;
  readonly wipLimit: number | null;
}

export interface BoardTask {
  readonly taskId: string;
  readonly boardId: string;
  readonly columnId: string;
  readonly title: string;
  readonly description: string | null;
  readonly status: "backlog" | "ready" | "in_progress" | "blocked" | "review" | "done" | "cancelled";
  readonly priority: "low" | "normal" | "high" | "urgent";
  readonly position: number;
  readonly assigneeKind: "human" | "agent" | null;
  readonly assigneeAgentId: string | null;
  readonly assigneeUserId: string | null;
  readonly completionCriteria: string | null;
  readonly revision: number;
  readonly archivedAt: string | null;
}

export interface BoardDispatch {
  readonly dispatchId: string;
  readonly taskId: string;
  readonly agentId: string;
  readonly taskRevision: number;
  readonly runId: string | null;
  readonly branchId: string | null;
  readonly state: "dispatched" | "running" | "settled" | "failed" | "cancelled";
  readonly attempt: number;
  readonly error: string | null;
  readonly expiresAt: string;
}

export interface BoardDispatchView {
  readonly dispatch: BoardDispatch;
  /** The run's own state and real usage while the dispatch is live; null once settled. */
  readonly run: { readonly state: string; readonly pauseReason: string | null; readonly usage: { readonly inputTokens: number; readonly outputTokens: number; readonly toolCalls: number } } | null;
}

export interface BoardTaskReadiness {
  readonly ready: boolean;
  readonly reasons: readonly string[];
  readonly dependsOn: readonly string[];
  readonly blocks: readonly string[];
}

export interface BoardTaskView {
  readonly task: BoardTask;
  readonly readiness: BoardTaskReadiness;
  readonly messageCount: number;
  readonly artifactCount: number;
  /** Unanswered questions another task addressed to this one. */
  readonly openQuestions: number;
  /** Durable, still-unanswered human input requests on the task's live run. */
  readonly pendingInput: number;
}

export interface BoardView {
  readonly board: BoardRecord;
  readonly columns: readonly BoardColumn[];
  readonly tasks: readonly BoardTaskView[];
  readonly counts: { readonly total: number; readonly active: number; readonly ready: number; readonly doneByDependency: number };
  readonly dispatches: readonly BoardDispatchView[];
}

export interface BoardArtifact {
  readonly artifactId: string;
  readonly taskId: string;
  readonly kind: "input" | "output" | "reference";
  readonly fileId: string | null;
  readonly snapshotFileId: string | null;
  readonly path: string;
  readonly hash: string | null;
  readonly size: number | null;
  readonly mimeType: string | null;
  readonly runId: string | null;
  readonly agentId: string | null;
  readonly createdAt: string;
}

/**
 * A board working copy plus the workspace file it was copied from and that file's
 * current version, so a client can offer a confirmed "Publish to workspace" action
 * and report a revision conflict instead of guessing the expected version.
 */
export interface BoardTaskFile extends BoardArtifact {
  readonly sourcePath: string | null;
  readonly sourceVersion: number | null;
}

export interface BoardMessage {
  readonly messageId: string;
  readonly taskId: string;
  readonly authorKind: "user" | "agent" | "system";
  readonly authorUserId: string | null;
  readonly authorAgentId: string | null;
  readonly runId: string | null;
  readonly kind: "comment" | "question" | "handoff" | "progress" | "result";
  readonly body: string;
  readonly createdAt: string;
}

/** Public call metadata; join credentials are only returned to the answering device. */
export interface CallSessionView {
  readonly sessionId: string;
  readonly requestId: string | null;
  readonly conversationId: string;
  readonly branchId: string;
  readonly runId: string;
  readonly state: "ringing" | "connected" | "ended" | "declined" | "missed" | "cancelled" | "expired";
  readonly attempt: number;
  readonly ringExpiresAt: string;
  readonly answeredByDeviceId: string | null;
  readonly declinedByDeviceId: string | null;
  readonly connectedAt: string | null;
  readonly durationSeconds: number | null;
  readonly endedReason: string | null;
  readonly joinTokenExpiresAt: string | null;
  readonly createdAt: string;
}

export interface CallAnswerResponse {
  readonly session: CallSessionView;
  readonly joinToken: string;
  /** Coordination alone does not provide an audio connection. */
  readonly media: { readonly transport: "webrtc"; readonly status: "not-implemented" };
}

export interface CallPreferenceSettings {
  readonly callEnabled: boolean;
  readonly preferredDeviceIds: readonly string[];
  readonly quietStartHour: number | null;
  readonly quietEndHour: number | null;
  readonly quietTimezoneOffsetMinutes: number | null;
  readonly snoozeUntil: string | null;
  readonly updatedAt: string | null;
}

export interface FormAnswerSubmission {
  readonly answers: Readonly<Record<string, unknown>>;
  /** Field ids whose values must be sealed rather than sent as model-visible text. */
  readonly protectedAnswers?: readonly { readonly fieldId: string; readonly label?: string; readonly value: string }[];
  /** Credential-like fields the user explicitly confirmed are ordinary text. */
  readonly ordinaryFieldIds?: readonly string[];
  /** Revision of the form being answered; the server refuses a stale one. */
  readonly formRevision?: string;
}

export interface CreateAgentInput {
  readonly name: string;
  readonly description?: string;
  readonly systemPrompt?: string;
  readonly personality?: string | null;
  readonly personalities?: import("@dayvise/domain").Personalities | null;
  readonly modelProvider?: string;
  readonly modelName?: string;
  readonly shared?: boolean;
}

export interface UpdateAgentInput {
  readonly name?: string;
  readonly description?: string | null;
  readonly systemPrompt?: string | null;
  readonly personality?: string | null;
  readonly personalities?: import("@dayvise/domain").Personalities | null;
  readonly modelProvider?: string | null;
  readonly modelName?: string | null;
  readonly shared?: boolean;
}

export interface CreateScheduleInput {
  readonly agentId: AgentId | string;
  readonly title: string;
  readonly prompt: string;
  /** Either a cron expression or a one-shot `runAt`, never both. */
  readonly cron?: string;
  readonly runAt?: string;
  readonly timezone?: string;
  readonly overlapPolicy?: "queue" | "skip" | "parallel";
  readonly missedRunPolicy?: "skip" | "run_once" | "run_all";
  readonly notifyOnCompletion?: boolean;
}

export interface UpdateScheduleInput {
  readonly title?: string;
  readonly prompt?: string;
  readonly cron?: string | null;
  readonly runAt?: string | null;
  readonly timezone?: string;
  readonly overlapPolicy?: "queue" | "skip" | "parallel";
  readonly missedRunPolicy?: "skip" | "run_once" | "run_all";
  readonly notifyOnCompletion?: boolean;
}

export interface CreateMemoryInput {
  readonly content: string;
  readonly scope?: "private" | "shared";
  readonly agentId?: AgentId | string;
}

export interface CreateCredentialInput {
  readonly kind: "ai_provider" | "byo_database" | "byo_blob" | "secret";
  readonly provider: string;
  readonly label: string;
  /** Field name to value; encrypted server-side and never returned again. */
  readonly secret: Readonly<Record<string, string>>;
}

export interface RequestOptions {
  readonly signal?: AbortSignal;
}

export interface DayviseClient {
  readonly baseUrl: string;
  readonly http: HttpTransport;
  readonly auth: {
    /** Read the current session (user, plane, workspaces with roles). */
    session(options?: RequestOptions): Promise<AuthSession>;
    /** Move this session to another workspace the user belongs to (server-enforced). */
    switchWorkspace(workspaceId: WorkspaceId | string, options?: RequestOptions): Promise<{ activeWorkspaceId: string }>;
    /** Revoke the session server-side. Best-effort from sign-out flows. */
    logout(options?: RequestOptions): Promise<{ revoked: boolean }>;
  };
  readonly conversations: {
    list(options?: RequestOptions): Promise<{ conversations: readonly Conversation[]; total: number }>;
    create(input: CreateConversationInput, options?: RequestOptions): Promise<CreateConversationResponse>;
    get(conversationId: ConversationId | string, options?: RequestOptions): Promise<ConversationDetail>;
    /**
     * Delete a conversation and choose what happens to its derived memories.
     * `relatedMemories` is required: the server refuses a delete that does not
     * state its policy (prompt.md §8.4).
     */
    delete(
      conversationId: ConversationId | string,
      input: { relatedMemories: "keep" | "delete" },
      options?: RequestOptions,
    ): Promise<DeleteConversationResult>;
    messages(conversationId: ConversationId | string, query?: { branchId?: string } & RequestOptions): Promise<MessageListResponse>;
    downloadTranscript(conversationId: ConversationId | string, query?: { branchId?: string; format?: TranscriptFormat; redact?: boolean } & RequestOptions): Promise<DownloadContent>;
    listOutputs(conversationId: ConversationId | string, query?: { branchId?: string } & RequestOptions): Promise<{ tree: ConversationOutputTree }>;
    downloadOutput(conversationId: ConversationId | string, snapshotFileId: string, query?: { branchId?: string } & RequestOptions): Promise<DownloadContent>;
    outputPreview(conversationId: ConversationId | string, snapshotFileId: string, query?: { branchId?: string } & RequestOptions): Promise<{ preview: OutputRestorePreview }>;
    restoreOutput(
      conversationId: ConversationId | string,
      snapshotFileId: string,
      input: { confirm: boolean; expectedVersion?: number; branchId?: string },
      options?: RequestOptions,
    ): Promise<OutputRestoreResult>;
    send(conversationId: ConversationId | string, input: SendMessageInput, options?: RequestOptions): Promise<SendMessageOutcome>;
  };
  readonly runs: {
    list(options?: RequestOptions): Promise<{ runs: readonly Run[] }>;
    active(options?: RequestOptions): Promise<{ runs: readonly Run[] }>;
    get(runId: RunId | string, options?: RequestOptions): Promise<RunDetail>;
    events(runId: RunId | string, afterSequence: number, options?: RequestOptions): Promise<ResumeResponse>;
    steer(runId: RunId | string, message: string, options?: RequestOptions): Promise<{ controlId: string; sequence: number }>;
    pause(runId: RunId | string, reason?: string, options?: RequestOptions): Promise<{ run: Run }>;
    resume(
      runId: RunId | string,
      checkpointId?: string | null,
      options?: RequestOptions,
    ): Promise<{ runtimeId: string; reusedWarm: boolean; resumedFrom: string | null }>;
    cancel(runId: RunId | string, reason?: string, options?: RequestOptions): Promise<{ run: Run }>;
    manifest(runId: RunId | string, options?: RequestOptions): Promise<{ manifest: ClientRunManifest }>;
    approvals(runId: RunId | string, options?: RequestOptions): Promise<ApprovalList>;
    diff(runId: RunId | string, options?: RequestOptions): Promise<{ diff: RunDiff }>;
    decideApproval(
      runId: RunId | string,
      approvalId: string,
      decision: "approved" | "rejected",
      note?: string,
      options?: RequestOptions,
    ): Promise<{ approval: Approval }>;
    answerClarification(
      runId: RunId | string,
      clarificationId: string,
      answers: Record<string, unknown>,
      options?: RequestOptions,
    ): Promise<ClarificationAnswerOutcome>;
    /**
     * Mint a 60-second ticket for the realtime socket. Browsers cannot set an
     * Authorization header on a WebSocket upgrade, so the socket presents this
     * instead (`GET /v1/runs/:runId/stream?ticket=…`). Socket-only credential.
     */
    streamTicket(runId: RunId | string, options?: RequestOptions): Promise<{ ticket: string; expiresAt: string }>;
  };
  readonly agents: {
    list(options?: RequestOptions): Promise<{ agents: readonly Agent[] }>;
    create(input: CreateAgentInput, options?: RequestOptions): Promise<{ agent: Agent }>;
    get(agentId: AgentId | string, options?: RequestOptions): Promise<{ agent: Agent }>;
    update(agentId: AgentId | string, input: UpdateAgentInput, options?: RequestOptions): Promise<{ agent: Agent }>;
    share(agentId: AgentId | string, options?: RequestOptions): Promise<{ agent: Agent }>;
    unshare(agentId: AgentId | string, options?: RequestOptions): Promise<{ agent: Agent }>;
    delete(agentId: AgentId | string, options?: RequestOptions): Promise<{ agent: Agent; archived: boolean }>;
  };
  readonly schedules: {
    list(options?: RequestOptions): Promise<{ schedules: readonly Schedule[] }>;
    create(input: CreateScheduleInput, options?: RequestOptions): Promise<{ schedule: Schedule }>;
    get(scheduleId: ScheduleId | string, options?: RequestOptions): Promise<{ schedule: Schedule }>;
    update(scheduleId: ScheduleId | string, input: UpdateScheduleInput, options?: RequestOptions): Promise<{ schedule: Schedule }>;
    trigger(scheduleId: ScheduleId | string, options?: RequestOptions): Promise<{ runId: RunId }>;
    enable(scheduleId: ScheduleId | string, options?: RequestOptions): Promise<void>;
    disable(scheduleId: ScheduleId | string, options?: RequestOptions): Promise<void>;
    remove(scheduleId: ScheduleId | string, options?: RequestOptions): Promise<void>;
  };
  readonly memory: {
    list(options?: RequestOptions): Promise<MemoryList>;
    create(input: CreateMemoryInput, options?: RequestOptions): Promise<{ memory: MemoryRecord }>;
    confirm(memoryId: string, input: { version: number; content?: string }, options?: RequestOptions): Promise<{ memory: MemoryRecord }>;
    /** Explicit user action; the agent can never promote memory (prompt.md §8.2). */
    promote(memoryId: string, options?: RequestOptions): Promise<{ memory: MemoryRecord }>;
    remove(memoryId: string, options?: RequestOptions): Promise<void>;
  };
  readonly files: {
    list(query?: { prefix?: string } & RequestOptions): Promise<{ files: readonly FileSummary[] }>;
    upload(input: { path: string; bytes: Uint8Array; contentType: string; conversationId?: string }, options?: RequestOptions): Promise<{ file: { fileId: string; path: string; version: number; size: number; storage: "structured" | "blob" } }>;
    download(fileId: string, options?: RequestOptions): Promise<FileContent>;
  };
  readonly credentials: {
    list(options?: RequestOptions): Promise<CredentialList>;
    create(input: CreateCredentialInput, options?: RequestOptions): Promise<{ credential: Credential }>;
    test(credentialId: string, options?: RequestOptions): Promise<ConnectionTestResult>;
    revoke(credentialId: string, options?: RequestOptions): Promise<void>;
  };
  readonly usage: {
    get(query?: { since?: Timestamp } & RequestOptions): Promise<UsageRollup>;
  };
  readonly learning: {
    events: {
      list(query?: { limit?: number; kind?: string; status?: string } & RequestOptions): Promise<{ events: readonly LearningEvent[]; kind: string | null; status: string | null }>;
    };
    refinements: {
      list(query?: { limit?: number } & RequestOptions): Promise<{ refinements: readonly LearningRefinement[] }>;
      get(refinementId: string, options?: RequestOptions): Promise<{ refinement: LearningRefinement }>;
      create(
        input: { targetKind: LearningRefinementTargetKind; targetId: string; conversationId: string; branchId?: string | null },
        options?: RequestOptions,
      ): Promise<{ refinement: LearningRefinement; queued: boolean; boundary: string }>;
      apply(refinementId: string, options?: RequestOptions): Promise<{ refinement: LearningRefinement; boundary: string }>;
      revert(refinementId: string, options?: RequestOptions): Promise<{ refinement: LearningRefinement }>;
    };
  };
  readonly snapshots: {
    list(options?: RequestOptions): Promise<{ snapshots: readonly WorkspaceSnapshotView[] }>;
    create(options?: RequestOptions): Promise<{ snapshot: WorkspaceSnapshotView; created: boolean }>;
    get(snapshotId: string, options?: RequestOptions): Promise<{ snapshot: WorkspaceSnapshotView }>;
    restore(snapshotId: string, input: { confirm: boolean }, options?: RequestOptions): Promise<SnapshotRestoreResult>;
    remove(snapshotId: string, options?: RequestOptions): Promise<void>;
  };
  readonly automationTemplates: {
    list(options?: RequestOptions): Promise<{ templates: readonly AutomationTemplateView[] }>;
    create(
      input: { name: string; prompt: string; cron?: string; runAt?: string; timezone?: string; parameters?: readonly AutomationTemplateParameterView[]; version?: string },
      options?: RequestOptions,
    ): Promise<{ template: AutomationTemplateView; created: boolean }>;
    get(templateId: string, options?: RequestOptions): Promise<{ template: AutomationTemplateView }>;
    use(templateId: string, input: { agentId: string; parameters?: Record<string, unknown>; title?: string }, options?: RequestOptions): Promise<TemplateUseResult>;
    remove(templateId: string, options?: RequestOptions): Promise<void>;
  };
  readonly bundles: {
    list(options?: RequestOptions): Promise<{ bundles: readonly SkillBundleView[] }>;
    create(input: { name: string; skillNames: readonly string[] }, options?: RequestOptions): Promise<{ bundle: SkillBundleView }>;
    get(bundleId: string, options?: RequestOptions): Promise<{ bundle: SkillBundleView; preview: BundleInstallPreview }>;
    install(bundleId: string, input: { confirm: true; expectedVersions: Readonly<Record<string, string | null>> }, options?: RequestOptions): Promise<{ installed: true; bundleId: string; skills: number; preview: BundleInstallPreview }>;
    remove(bundleId: string, options?: RequestOptions): Promise<void>;
  };
  readonly entitlements: {
    get(options?: RequestOptions): Promise<EntitlementsSummary>;
  };
  readonly audit: {
    list(options?: RequestOptions): Promise<{ entries: readonly AuditEntry[] }>;
  };
  readonly notifications: {
    list(options?: RequestOptions): Promise<{ notifications: readonly NotificationRecord[] }>;
    /** Mark one notification read, or all when no id is given (prompt.md §78). */
    markRead(notificationId?: string, options?: RequestOptions): Promise<{ marked: number }>;
  };
  readonly mcp: {
    list(options?: RequestOptions): Promise<{ servers: readonly McpServer[] }>;
    create(
      input: {
        name: string;
        transport?: McpTransport;
        url?: string | null;
        authType?: McpAuthType;
        credentialId?: string | null;
        /** Sealed immediately and never returned; used only for a bearer MCP server. */
        credentialValue?: string;
        shared?: boolean;
      },
      options?: RequestOptions,
    ): Promise<{ server: McpServer }>;
    get(serverId: string, options?: RequestOptions): Promise<{ server: McpServer }>;
    /**
     * Change an existing server. `enabled` is the on/off switch the tools UI uses;
     * a stdio executable is configured by the runtime operator and cannot be edited here.
     */
    update(
      serverId: string,
      input: {
        name?: string;
        shared?: boolean;
        enabled?: boolean;
        authType?: McpAuthType;
        credentialId?: string | null;
        credentialValue?: string;
      },
      options?: RequestOptions,
    ): Promise<{ server: McpServer }>;
    remove(serverId: string, options?: RequestOptions): Promise<void>;
    tools(serverId: string, options?: RequestOptions): Promise<{ tools: readonly McpToolDescriptor[] }>;
    test(serverId: string, options?: RequestOptions): Promise<{ ok: boolean; tools: readonly McpToolDescriptor[] }>;
  };
  readonly skills: {
    /** `includeArchived` asks for the lifecycle view, where a disabled skill can be restored. */
    list(query?: { readonly includeArchived?: boolean } & RequestOptions): Promise<{ skills: readonly Skill[] }>;
    create(
      input: {
        name: string;
        instructions: string;
        visibility?: SkillVisibility;
        tags?: readonly string[];
        requiredTools?: readonly string[];
      },
      options?: RequestOptions,
    ): Promise<{ skill: Skill }>;
    get(skillId: string, options?: RequestOptions): Promise<{ skill: Skill }>;
    update(
      skillId: string,
      input: {
        expectedVersion: string;
        name?: string;
        instructions?: string;
        document?: string;
        visibility?: SkillVisibility;
        tags?: readonly string[];
        requiredTools?: readonly string[];
      },
      options?: RequestOptions,
    ): Promise<{ skill: Skill }>;
    remove(skillId: string, options?: RequestOptions): Promise<{ archived?: boolean; skill?: Skill }>;
    restore(skillId: string, options?: RequestOptions): Promise<{ skill: Skill; restored: boolean }>;
    document(skillId: string, options?: RequestOptions): Promise<{ document: string }>;
    versions(skillId: string, options?: RequestOptions): Promise<{ versions: readonly SkillVersionRecord[] }>;
    share(skillId: string, input: { targetWorkspaceId: string }, options?: RequestOptions): Promise<{ share: SkillShareView }>;
    unshare(skillId: string, input: { shareId: string }, options?: RequestOptions): Promise<{ revoked: boolean; shareId: string }>;
  };
  readonly skillShares: {
    targets(options?: RequestOptions): Promise<{ workspaces: readonly SkillShareTargetView[] }>;
    owned(options?: RequestOptions): Promise<{ shares: readonly SkillShareView[] }>;
    incoming(options?: RequestOptions): Promise<{ shares: readonly SkillShareView[] }>;
    accept(shareId: string, input: { followUpdates: boolean }, options?: RequestOptions): Promise<{ accepted: boolean; shareId: string; skillId: string; version: string; followUpdates: boolean }>;
    update(shareId: string, input: { expectedTargetVersion: string | null; followUpdates: boolean }, options?: RequestOptions): Promise<{ updated: boolean; shareId: string; followUpdates: boolean; targetVersion: string | null }>;
    revoke(shareId: string, options?: RequestOptions): Promise<{ revoked: boolean; shareId: string }>;
  };
  readonly curator: {
    overview(options?: RequestOptions): Promise<CuratorOverview>;
    /** Download one explicitly selected skill as portable Markdown. */
    exportSkill(skillId: string, options?: RequestOptions): Promise<DownloadContent>;
  };
  readonly tools: {
    list(options?: RequestOptions): Promise<WorkspaceToolCatalogue>;
  };
  readonly admin: {
    members(options?: RequestOptions): Promise<{ members: readonly Membership[] }>;
    addMember(input: { userId: string; role: MembershipRole }, options?: RequestOptions): Promise<{ membership: Membership }>;
    removeMember(userId: string, options?: RequestOptions): Promise<{ removed: boolean }>;
    runRetention(options?: RequestOptions): Promise<RetentionResult>;
    sharedMemories(options?: RequestOptions): Promise<{ memories: readonly MemoryRecord[] }>;
    removeSharedMemory(memoryId: string, options?: RequestOptions): Promise<{ deleted: boolean }>;
    /** Workspace tool-grant rules (prompt.md §24). */
    toolPolicies(options?: RequestOptions): Promise<{ policies: readonly AdminToolPolicy[] }>;
    setToolPolicy(
      input: { toolId: string; effect: AdminToolPolicy["effect"]; riskCeiling?: string | null },
      options?: RequestOptions,
    ): Promise<{ policy: AdminToolPolicy }>;
    removeToolPolicy(toolId: string, options?: RequestOptions): Promise<{ removed: boolean }>;
  };
  readonly storage: {
    getRoute(options?: RequestOptions): Promise<StorageRouteResponse>;
    setRoute(
      input: {
        structured?: "d1_shared" | "postgres_byo";
        blob?: "r2_managed" | "s3_byo";
        structuredCredentialId?: string | null;
        blobCredentialId?: string | null;
        config?: unknown;
      },
      options?: RequestOptions,
    ): Promise<{ ok: boolean; route: StorageRouteResponse }>;
    startMigration(
      input: {
        direction: "d1_to_postgres" | "postgres_to_d1" | "r2_to_s3" | "s3_to_r2";
        targetCredentialId?: string;
        targetConfig?: unknown;
      },
      options?: RequestOptions,
    ): Promise<{ ok: boolean; job: StorageMigrationJobResponse }>;
    getMigrationJob(jobId: string, options?: RequestOptions): Promise<{ job: StorageMigrationJobResponse }>;
  };
  readonly billing: {
    get(options?: RequestOptions): Promise<BillingOverview>;
    portal(options?: RequestOptions): Promise<{ url: string }>;
    cancel(options?: RequestOptions): Promise<{ ok: true }>;
    preview(plan: string, options?: RequestOptions): Promise<{ intentId: string; total: string; recurringTotal: string; currency: string }>;
    change(intentId: string, options?: RequestOptions): Promise<{ ok: true }>;
    history(options?: RequestOptions): Promise<{ records: readonly { occurredAt: string; provider: string; transactionId: string; status: string; currency: string | null; total: string | null }[] }>;
    export(options?: RequestOptions): Promise<ArrayBuffer>;
    abandon(options?: RequestOptions): Promise<{ ok: boolean }>;
    deleteAccount(options?: RequestOptions): Promise<{ ok: true }>;

    checkout(input: { plan: string }, options?: RequestOptions): Promise<BillingCheckoutResult>;
    verifyPlayPurchase(
      input: { plan: "plus" | "pro" | "max" | "ultra" | "teams"; purchaseToken: string },
      options?: RequestOptions,
    ): Promise<PlayPurchaseVerificationResult>;
  };
  readonly worktrees: {
    list(conversationId: string, query?: { branchId?: string } & RequestOptions): Promise<{ worktrees: readonly WorktreeState[] }>;
  };
  readonly providerIdentities: {
    status(provider: string, options?: RequestOptions): Promise<ProviderIdentityStatusResponse>;
  };
  readonly home: {
    get(options?: RequestOptions): Promise<HomeResolution>;
    set(defaultWorkspaceId: string | null, options?: RequestOptions): Promise<HomeResolution>;
  };
  readonly projectInits: {
    directories(conversationId: string, branchId?: string | null, options?: RequestOptions): Promise<{ directories: readonly string[]; filesSelected: number }>;
    list(options?: RequestOptions): Promise<{ jobs: readonly ProjectInitItem[] }>;
    create(input: { conversationId: string; branchId?: string | null; workingDirectory?: string }, options?: RequestOptions): Promise<{ job: ProjectInitItem; created: boolean }>;
    get(jobId: string, options?: RequestOptions): Promise<{ job: ProjectInitItem }>;
    apply(jobId: string, input: { confirm: true; content: string; candidateSha256: string }, options?: RequestOptions): Promise<{ job: ProjectInitItem; fileId: string; version: number }>;
    discard(jobId: string, options?: RequestOptions): Promise<{ discarded: boolean }>;
  };
  readonly devices: {
    list(options?: RequestOptions): Promise<{ devices: readonly DeviceItem[]; /** The server's VAPID public key, when web push is configured. */ webPushPublicKey?: string }>;
    register(input: { platform: "android" | "ios" | "web" | "desktop"; token?: string; installationId?: string }, options?: RequestOptions): Promise<{ device: DeviceItem }>;
    setPushToken(deviceId: string, token: string, options?: RequestOptions): Promise<{ device: DeviceItem }>;
    clearPushToken(deviceId: string, options?: RequestOptions): Promise<{ device: DeviceItem }>;
    revoke(deviceId: string, options?: RequestOptions): Promise<{ revoked: boolean }>;
  };
  readonly inputRequests: {
    list(options?: RequestOptions): Promise<{ now: string; requests: readonly InputRequest[] }>;
    answer(runId: string, requestId: string, answers: Readonly<Record<string, unknown>>, options?: RequestOptions): Promise<{ ok: boolean; clarificationId: string; sequence: number }>;
    /**
     * Answer a structured form, including values the user chose to protect.
     * Protected values are sealed server-side; the run only ever sees a handle.
     */
    answerForm(runId: string, requestId: string, submission: FormAnswerSubmission, options?: RequestOptions): Promise<{ ok: boolean; clarificationId: string; sequence: number }>;
    decline(runId: string, requestId: string, options?: RequestOptions): Promise<{ ok: boolean; declined: boolean; clarificationId: string; sequence: number }>;
  };
  readonly presence: {
    list(options?: RequestOptions): Promise<{ now: string; leases: readonly PresenceLease[] }>;
    update(input: {
      deviceId: string;
      state: "foreground" | "hidden" | "minimized" | "background" | "locked";
      callCapable: boolean;
      sequence: number;
      interactedAt: string;
    }, options?: RequestOptions): Promise<{ accepted: boolean; observedAt: string; expiresAt: string }>;
  };
  readonly calls: {
    list(options?: RequestOptions): Promise<{ sessions: readonly CallSessionView[] }>;
    get(sessionId: string, options?: RequestOptions): Promise<{ session: CallSessionView }>;
    ring(input: { requestId: string }, options?: RequestOptions): Promise<{ session: CallSessionView; created: boolean }>;
    answer(sessionId: string, input: { deviceId: string }, options?: RequestOptions): Promise<CallAnswerResponse>;
    decline(sessionId: string, input: { deviceId?: string; snoozeMinutes?: number }, options?: RequestOptions): Promise<{ session: CallSessionView }>;
    hangup(sessionId: string, options?: RequestOptions): Promise<{ session: CallSessionView }>;
  };
  readonly callPreferences: {
    /** Which devices may be rung and when; presence routing reads exactly this record. */
    get(options?: RequestOptions): Promise<{ scopeKey: string; preferences: CallPreferenceSettings }>;
    update(input: Partial<Omit<CallPreferenceSettings, "updatedAt">>, options?: RequestOptions): Promise<{ scopeKey: string; preferences: CallPreferenceSettings }>;
    reset(options?: RequestOptions): Promise<{ scopeKey: string; preferences: CallPreferenceSettings }>;
  };
  readonly yolo: {
    /** Elevated-autonomy preferences. This mode grants nothing: grants and approvals still decide. */
    get(options?: RequestOptions): Promise<{ mode: YoloModeState }>;
    update(input: { scopeKind: "user" | "workspace"; enabled: boolean; reason?: string }, options?: RequestOptions): Promise<{ mode: YoloModeState }>;
  };
  readonly boards: {
    /** Boards this member can read, including archived ones when asked. */
    list(query?: { readonly includeArchived?: boolean } & RequestOptions): Promise<{ boards: readonly BoardRecord[] }>;
    summary(options?: RequestOptions): Promise<{ summary: string }>;
    create(input: { name: string; description?: string | null; shareWithWorkspace?: boolean; confirmSharing?: boolean }, options?: RequestOptions): Promise<{ board: BoardRecord }>;
    get(boardId: string, query?: { readonly includeArchived?: boolean } & RequestOptions): Promise<{ view: BoardView }>;
    update(boardId: string, input: { name?: string; description?: string | null; visibility?: "private" | "workspace"; confirmSharing?: boolean; expectedRevision?: number }, options?: RequestOptions): Promise<{ board: BoardRecord }>;
    archive(boardId: string, options?: RequestOptions): Promise<{ board: BoardRecord }>;
    restore(boardId: string, options?: RequestOptions): Promise<{ board: BoardRecord }>;
    addColumn(boardId: string, input: { name: string; wipLimit?: number | null; position?: number }, options?: RequestOptions): Promise<{ column: BoardColumn }>;
    updateColumn(boardId: string, columnId: string, input: { name?: string; wipLimit?: number | null; position?: number }, options?: RequestOptions): Promise<{ column: BoardColumn }>;
    removeColumn(boardId: string, columnId: string, options?: RequestOptions): Promise<{ removed: boolean }>;
    addTask(boardId: string, input: {
      title: string;
      description?: string | null;
      columnId?: string;
      priority?: "low" | "normal" | "high" | "urgent";
      status?: BoardTask["status"];
      assigneeKind?: "human" | "agent" | null;
      assigneeAgentId?: string;
      assigneeUserId?: string;
      completionCriteria?: string | null;
    }, options?: RequestOptions): Promise<{ task: BoardTask }>;
    updateTask(boardId: string, taskId: string, input: {
      title?: string;
      description?: string | null;
      status?: BoardTask["status"];
      priority?: BoardTask["priority"];
      assigneeKind?: "human" | "agent" | null;
      assigneeAgentId?: string | null;
      assigneeUserId?: string | null;
      completionCriteria?: string | null;
      criteriaMet?: boolean;
      archived?: boolean;
      expectedRevision?: number;
    }, options?: RequestOptions): Promise<{ task: BoardTask }>;
    moveTask(boardId: string, taskId: string, input: { columnId: string; position?: number; expectedRevision?: number }, options?: RequestOptions): Promise<{ task: BoardTask }>;
    addDependency(boardId: string, taskId: string, dependsOnTaskId: string, options?: RequestOptions): Promise<{ dependency: { taskId: string; dependsOnTaskId: string } }>;
    removeDependency(boardId: string, taskId: string, dependsOnTaskId: string, options?: RequestOptions): Promise<{ removed: boolean }>;
    messages(boardId: string, taskId: string, options?: RequestOptions): Promise<{ messages: readonly BoardMessage[] }>;
    inbox(boardId: string, taskId: string, options?: RequestOptions): Promise<{ messages: readonly BoardMessage[] }>;
    postMessage(
      boardId: string,
      taskId: string,
      input: { kind?: BoardMessage["kind"]; body: string; recipientTaskId?: string; replyToMessageId?: string },
      options?: RequestOptions,
    ): Promise<{ message: BoardMessage }>;
    files(boardId: string, taskId: string, options?: RequestOptions): Promise<{ files: readonly BoardTaskFile[] }>;
    copyFile(boardId: string, taskId: string, input: { snapshotFileId: string; name?: string }, options?: RequestOptions): Promise<{ artifact: BoardArtifact }>;
    removeFile(boardId: string, taskId: string, artifactId: string, options?: RequestOptions): Promise<{ removed: boolean }>;
    publishFile(boardId: string, taskId: string, artifactId: string, input: { confirm: true; expectedVersion: number }, options?: RequestOptions): Promise<{ path: string; version: number; sourcePath: string }>;
    artifacts(boardId: string, taskId: string, options?: RequestOptions): Promise<{ artifacts: readonly BoardArtifact[] }>;
    linkArtifact(boardId: string, taskId: string, input: { path: string; kind?: BoardArtifact["kind"]; snapshotFileId?: string; runId?: string; agentId?: string }, options?: RequestOptions): Promise<{ artifact: BoardArtifact }>;
    removeArtifact(boardId: string, taskId: string, artifactId: string, options?: RequestOptions): Promise<{ removed: boolean }>;
    dispatch(boardId: string, taskId: string, input?: { agentId?: string; expectedRevision?: number }, options?: RequestOptions): Promise<{ dispatch: BoardDispatch; runId: string | null; branchId: string | null }>;
    cancelTask(boardId: string, taskId: string, input?: { reason?: string }, options?: RequestOptions): Promise<{ cancelled: boolean }>;
    settleDispatches(boardId: string, options?: RequestOptions): Promise<{ settled: number; failed: number; cancelled: number }>;
  };
}

export interface StorageRouteResponse {
  readonly routeId?: string;
  readonly workspaceId?: string;
  readonly structured: "d1_shared" | "postgres_byo";
  readonly blob: "r2_managed" | "s3_byo";
  readonly shardKey?: string | null;
  readonly structuredCredentialId?: string | null;
  readonly blobCredentialId?: string | null;
  readonly config?: unknown;
  readonly health: "healthy" | "degraded" | "unavailable";
  readonly lastCheckedAt: string | null;
}

export interface StorageMigrationJobResponse {
  readonly jobId: string;
  readonly workspaceId: string;
  readonly direction: "d1_to_postgres" | "postgres_to_d1" | "r2_to_s3" | "s3_to_r2";
  readonly status: "pending" | "running" | "completed" | "failed";
  readonly progress: {
    readonly currentStep: string;
    readonly stepsCompleted: number;
    readonly totalSteps: number;
    readonly itemsMigrated: number;
    readonly totalItems: number;
    readonly checksums: Record<string, string>;
    readonly targetCredentialId?: string;
  };
  readonly error: string | null;
  readonly startedAt: string;
  readonly completedAt: string | null;
}

export interface RetentionResult {
  readonly conversations: number;
  readonly messages: number;
  readonly branches: number;
  readonly files: number;
  readonly auditEntries: number;
  readonly maxRetentionDays: number;
  readonly auditRetentionDays: number;
}

export interface WorktreeState {
  readonly name: string;
  readonly path: string;
  readonly branch: string;
  readonly baseRef: string | null;
  readonly head: string | null;
  readonly state: string;
  readonly dirty: boolean | null;
  readonly changed: readonly string[];
  readonly changedCount: number | null;
  readonly ahead: number | null;
  readonly behind: number | null;
  readonly diffFiles: readonly string[];
}

export interface ProviderIdentityStatusResponse {
  readonly provider: string;
  readonly configured: boolean;
  readonly status: "linked" | "revoked" | "unlinked";
  readonly scopes: readonly string[];
  readonly consentAcceptedAt: string | null;
  readonly planKind: string | null;
  readonly accessTokenExpiresAt: string | null;
  readonly planFundedAvailable: boolean;
  readonly reason: string;
}

export interface HomeWorkspaceOption {
  readonly workspaceId: string;
  readonly name: string;
  readonly kind: string;
}

export interface HomeResolution {
  readonly scopeKey: string;
  readonly storedWorkspaceId: string | null;
  readonly resolvedWorkspaceId: string | null;
  readonly stale: boolean;
  readonly workspaces: readonly HomeWorkspaceOption[];
}

export interface ProjectInitItem {
  readonly jobId: string;
  readonly conversationId: string;
  readonly branchId: string;
  readonly workingDirectory: string;
  readonly state: "queued" | "scanning" | "candidate" | "failed" | "applied" | "discarded";
  readonly projectRevision: number;
  readonly candidateSha256: string;
  readonly candidatePreview: string;
  readonly candidateContent?: string;
  readonly existingContent?: string;
  readonly candidateBytes: number;
  readonly reason: string;
  readonly uncertainties: readonly string[];
  readonly sourceFiles: readonly { path: string; version: number; hash: string; size: number; mimeType: string }[];
  readonly scan: { filesPinned: number; filesRead: number; bytesRead: number; skipped: readonly string[]; truncated: boolean };
  readonly appliedFileId: string | null;
  readonly appliedVersion: number | null;
  readonly error: string | null;
  readonly createdAt: string;
}

export interface DeviceItem {
  readonly deviceId: string;
  readonly platform: "android" | "ios" | "web" | "desktop";
  readonly tokenMasked: string;
  readonly pushRegistered?: boolean;
  readonly createdAt?: string;
  readonly lastSeenAt?: string;
}

export interface ConversationOutput {
  readonly path: string;
  readonly fileId: string;
  readonly version: number;
  readonly snapshotFileId: string;
  readonly hash: string;
  readonly mimeType: string;
  readonly size: number;
  readonly runId: string;
  readonly branchId: string;
  readonly sequence: number;
}

export interface ConversationOutputTree {
  readonly conversationId: string;
  readonly branchId: string;
  readonly runId: string;
  readonly sequence: number;
  readonly files: readonly ConversationOutput[];
  readonly truncated: boolean;
}

export type DiffLineKind = "context" | "added" | "removed";

export interface RunDiffLine {
  readonly kind: DiffLineKind;
  readonly text: string;
  readonly beforeLine: number | null;
  readonly afterLine: number | null;
  readonly missingFinalNewline?: boolean;
}

export interface RunDiffText {
  readonly kind: "text";
  readonly lines: readonly RunDiffLine[];
  readonly added: number;
  readonly removed: number;
  readonly truncated: boolean;
}

export interface RunDiffBinary {
  readonly kind: "binary";
  readonly truncated: false;
}

export interface RunDiffSide {
  readonly sha256: string;
  readonly revision: number;
  readonly size: number;
}

export interface RunDiffFile {
  readonly path: string;
  readonly kind: "created" | "edited" | "deleted";
  readonly before: RunDiffSide | null;
  readonly after: RunDiffSide | null;
  readonly content: RunDiffText | RunDiffBinary;
}

export interface RunDiff {
  readonly conversationId: string;
  readonly branchId: string;
  readonly runId: string;
  readonly files: readonly RunDiffFile[];
  readonly truncated: boolean;
  readonly unpublished: boolean;
}

export interface InputRequest {
  readonly requestId: string;
  readonly runId: string;
  readonly conversationId: string;
  readonly branchId: string;
  readonly questions: readonly unknown[];
  readonly form: unknown | null;
  /** Revision of the declared form; the answer echoes it so a revision race is refused. */
  readonly formRevision?: string | null;
  /** What kind of input the durable row is waiting for, when it has one. */
  readonly requestKind?: string | null;
  /** Durable lifecycle state of the row; only `pending` rows may be answered. */
  readonly status?: string | null;
  /** Compare-and-swap revision of the durable row. */
  readonly revision?: number | null;
  readonly expiresAt: string | null;
}

export interface PresenceLease {
  readonly deviceId: string;
  readonly state: "foreground" | "hidden" | "minimized" | "background" | "locked";
  readonly callCapable: boolean;
  readonly interactedAt: string;
  readonly expiresAt: string;
}

export interface BillingCheckoutResult {
  readonly ok: true;
  readonly transactionId: string;
  readonly plan: string;
  readonly checkoutUrl: string;
}

export interface PlayPurchaseVerificationResult {
  readonly ok: true;
  readonly plan: "plus" | "pro" | "max" | "ultra" | "teams";
  readonly verifiedAt: string;
}

export interface BillingOverview {
  readonly accessActive?: boolean;
  readonly trialEligible?: boolean;
  readonly purchaseChannel?: "paddle" | "play" | null;
  readonly subscriptionStatus?: string | null;
  readonly scheduledCancellation?: string | null;
  readonly offboarding?: { state: string; failedAt: string; deletionAt: string | null; exportAvailable: boolean } | null;
  readonly workspaceId: string;
  readonly plan: string;
  readonly pricing: {
    readonly priceMonthlyUsd: number | null;
    readonly interval: "month" | null;
    readonly trial: {
      readonly trialDays: number;
      readonly requiresCard: boolean;
      readonly cancelAnytime: boolean;
      readonly description: string;
    } | null;
    readonly description: string;
  };
  readonly quotas: Record<string, number | null>;
  readonly features: Record<string, boolean>;
  readonly isConfigured: boolean;
  /** Whether the server can verify Google Play subscription purchases. */
  readonly playBillingConfigured?: boolean;
  /** Whether the authenticated workspace role may start or verify purchases. */
  readonly canManageBilling?: boolean;
  readonly plans: readonly {
    readonly id: string;
    readonly name: string;
    readonly audience: "individual" | "organization";
    readonly pricing: {
      readonly priceMonthlyUsd: number | null;
      readonly interval: "month" | null;
      readonly trial: {
        readonly trialDays: number;
        readonly requiresCard: boolean;
        readonly cancelAnytime: boolean;
        readonly description: string;
      } | null;
      readonly description: string;
    };
    readonly features: Record<string, boolean>;
    readonly quotas: Record<string, number | null>;
    /** Public Play Console product identifier, or null when unavailable. */
    readonly playProductId?: string | null;
  }[];
}

export interface DayviseClientConfig {
  /**
   * Full API base including the version segment, taken from the environment
   * configuration (`EndpointConfig.api`, prompt.md §39). Never assembled from a
   * hardcoded host inside a component.
   */
  readonly baseUrl: string;
  readonly getToken?: () => Promise<string | null> | string | null;
  readonly getWorkspace?: () => Promise<WorkspaceId | string | null> | WorkspaceId | string | null;
  readonly fetch?: FetchLike;
  readonly defaultHeaders?: Readonly<Record<string, string>>;
  readonly retry?: Partial<RetryPolicy>;
  /** Injected so backoff is deterministic in tests. */
  readonly random?: () => number;
  readonly sleep?: (ms: number, signal?: AbortSignal) => Promise<void>;
  readonly now?: () => number;
  readonly onResponse?: (observation: HttpObservation) => void;
}

function optionsOf(options: RequestOptions | undefined): HttpRequestOptions {
  return options?.signal ? { signal: options.signal } : {};
}

function bodyOptions(method: HttpRequestOptions["method"], body: unknown, options?: RequestOptions & { idempotencyKey?: string }): HttpRequestOptions {
  const base: HttpRequestOptions = { method, body, ...optionsOf(options) };
  return options?.idempotencyKey ? { ...base, idempotencyKey: options.idempotencyKey } : base;
}

/** For verbs whose body is intentionally empty, e.g. `DELETE` and `enable`. */
function methodOptions(method: HttpRequestOptions["method"], options?: RequestOptions): HttpRequestOptions {
  return { method, ...optionsOf(options) };
}

/** Session shape for hydration and account switching (prompt.md §71). */
export type AuthSession = SessionResponse;
export { SessionResponseSchema, type SessionResponse };

export function createDayviseClient(config: DayviseClientConfig): DayviseClient {
  const http = createHttpTransport({
    baseUrl: config.baseUrl,
    ...(config.getToken ? { getToken: config.getToken } : {}),
    ...(config.getWorkspace ? { getWorkspace: () => Promise.resolve(config.getWorkspace?.() ?? null) } : {}),
    ...(config.fetch ? { fetch: config.fetch } : {}),
    ...(config.defaultHeaders ? { defaultHeaders: config.defaultHeaders } : {}),
    ...(config.retry ? { retry: config.retry } : {}),
    ...(config.random ? { random: config.random } : {}),
    ...(config.sleep ? { sleep: config.sleep } : {}),
    ...(config.now ? { now: config.now } : {}),
    ...(config.onResponse ? { onResponse: config.onResponse } : {}),
  });

  async function rawArrayBuffer(path: string, options: RequestOptions | undefined, query?: HttpRequestOptions["query"]): Promise<DownloadContent> {
    const response = await http.requestRaw(path, { ...optionsOf(options), ...(query ? { query } : {}) });
    const disposition = response.headers.get("content-disposition") ?? "";
    return {
      bytes: await response.arrayBuffer(),
      mimeType: response.headers.get("content-type") ?? "application/octet-stream",
      filename: filenameFromDisposition(disposition),
    };
  }

  return {
    baseUrl: http.baseUrl,
    http,

    auth: {
      session: (options) => http.request<AuthSession>("/auth/session", optionsOf(options)),
      switchWorkspace: (workspaceId, options) =>
        http.request<{ activeWorkspaceId: string }>(
          "/auth/session/workspace",
          bodyOptions("POST", { workspaceId: String(workspaceId) }, options),
        ),
      logout: (options) => http.request<{ revoked: boolean }>("/auth/logout", bodyOptions("POST", {}, options)),
    },

    conversations: {
      list: (options) => http.request<{ conversations: readonly Conversation[]; total: number }>("/conversations", optionsOf(options)),
      create: (input, options) =>
        http.request<CreateConversationResponse>("/conversations", bodyOptions("POST", input, options)),
      get: async (conversationId, options) => {
        const raw = await http.request<ConversationDetail>(`/conversations/${encodeURIComponent(String(conversationId))}`, optionsOf(options));
        return { conversation: raw.conversation, branches: raw.branches ?? [], runs: raw.runs ?? [] };
      },
      messages: (conversationId, query) =>
        http.request<MessageListResponse>(`/conversations/${encodeURIComponent(String(conversationId))}/messages`, {
          ...optionsOf(query),
          query: { branchId: query?.branchId },
        }),
      downloadTranscript: (conversationId, query) => rawArrayBuffer(`/conversations/${encodeURIComponent(String(conversationId))}/export`, query, {
        branchId: query?.branchId, format: query?.format ?? "json", redact: query?.redact ?? true,
      }),
      listOutputs: (conversationId, query) =>
        http.request<{ tree: ConversationOutputTree }>(`/conversations/${encodeURIComponent(String(conversationId))}/outputs`, {
          ...optionsOf(query),
          query: query?.branchId === undefined ? {} : { branchId: query.branchId },
        }),
      downloadOutput: (conversationId, snapshotFileId, query) =>
        rawArrayBuffer(`/conversations/${encodeURIComponent(String(conversationId))}/outputs/${encodeURIComponent(snapshotFileId)}`, query, {
          ...(query?.branchId === undefined ? {} : { branchId: query.branchId }),
        }),
      outputPreview: (conversationId, snapshotFileId, query) =>
        http.request<{ preview: OutputRestorePreview }>(`/conversations/${encodeURIComponent(String(conversationId))}/outputs`, {
          ...optionsOf(query),
          query: {
            preview: snapshotFileId,
            ...(query?.branchId === undefined ? {} : { branchId: query.branchId }),
          },
        }),
      restoreOutput: (conversationId, snapshotFileId, input, options) =>
        http.request<OutputRestoreResult>(`/conversations/${encodeURIComponent(String(conversationId))}/outputs/${encodeURIComponent(snapshotFileId)}`, {
          method: "POST",
          body: {
            confirm: input.confirm,
            ...(input.expectedVersion === undefined ? {} : { expectedVersion: input.expectedVersion }),
          },
          ...optionsOf(options),
          query: input.branchId === undefined ? {} : { branchId: input.branchId },
        }),
      delete: (conversationId, input, options) =>
        http.request<DeleteConversationResult>(
          `/conversations/${encodeURIComponent(String(conversationId))}`,
          bodyOptions("DELETE", { relatedMemories: input.relatedMemories }, options),
        ),
      send: async (conversationId, input, options) => {
        const raw = await http.request<unknown>(
          `/conversations/${encodeURIComponent(String(conversationId))}/messages`,
          bodyOptions("POST", { text: input.text, workingDirectory: input.workingDirectory, interpretSlash: input.interpretSlash, branchId: input.branchId, model: input.model, secrets: input.secrets, processingMode: input.processingMode, attachments: input.attachments }, {
            ...options,
            ...(input.idempotencyKey ? { idempotencyKey: input.idempotencyKey } : {}),
          }),
        );
        return normalizeSendMessage(raw);
      },
    },

    runs: {
      list: (options) => http.request<{ runs: readonly Run[] }>("/runs", optionsOf(options)),
      active: (options) => http.request<{ runs: readonly Run[] }>("/runs?active=true", optionsOf(options)),
      get: async (runId, options) => {
        const raw = await http.request<{
          run: Run;
          toolCalls?: unknown[];
          transitions?: unknown[];
          checkpoint?: unknown;
        }>(`/runs/${encodeURIComponent(String(runId))}`, optionsOf(options));
        return {
          run: raw.run,
          toolCalls: (raw.toolCalls ?? []).map(normalizeToolCall).filter((call) => call !== null),
          transitions: (raw.transitions ?? []).map(normalizeRunTransition).filter((transition) => transition !== null),
          checkpoint: normalizeRunCheckpoint(raw.checkpoint ?? null),
        };
      },
      events: async (runId, afterSequence, options) => {
        const raw = await http.request<{ events?: unknown[]; snapshotRequired?: unknown; latestSequence?: unknown }>(
          `/runs/${encodeURIComponent(String(runId))}/events`,
          { ...optionsOf(options), query: { after: afterSequence } },
        );
        const events = (raw.events ?? []).map(normalizeRunEvent).filter((event): event is RunStreamEvent => event !== null);
        const latestFromEvents = events.reduce((max, event) => Math.max(max, event.sequence), afterSequence);
        return {
          events,
          snapshotRequired: raw.snapshotRequired === true,
          latestSequence: typeof raw.latestSequence === "number" ? raw.latestSequence : latestFromEvents,
        };
      },
      steer: (runId, message, options) =>
        http.request<{ controlId: string; sequence: number }>(
          `/runs/${encodeURIComponent(String(runId))}/steer`,
          bodyOptions("POST", { message }, options),
        ),
      pause: (runId, reason, options) =>
        http.request<{ run: Run }>(`/runs/${encodeURIComponent(String(runId))}/pause`, bodyOptions("POST", { reason }, options)),
      resume: (runId, checkpointId, options) =>
        http.request<{ runtimeId: string; reusedWarm: boolean; resumedFrom: string | null }>(
          `/runs/${encodeURIComponent(String(runId))}/resume`,
          bodyOptions("POST", { checkpointId: checkpointId ?? null }, options),
        ),
      cancel: (runId, reason, options) =>
        http.request<{ run: Run }>(`/runs/${encodeURIComponent(String(runId))}/cancel`, bodyOptions("POST", { reason }, options)),
      manifest: (runId, options) =>
        http.request<{ manifest: ClientRunManifest }>(`/runs/${encodeURIComponent(String(runId))}/manifest`, optionsOf(options)),
      approvals: async (runId, options) => {
        const raw = await http.request<{ approvals: readonly Approval[] }>(
          `/runs/${encodeURIComponent(String(runId))}/approvals`,
          optionsOf(options),
        );
        return { approvals: raw.approvals ?? [] };
      },
      diff: (runId, options) =>
        http.request<{ diff: RunDiff }>(`/runs/${encodeURIComponent(String(runId))}/diff`, optionsOf(options)),
      decideApproval: (runId, approvalId, decision, note, options) =>
        http.request<{ approval: Approval }>(
          `/runs/${encodeURIComponent(String(runId))}/approvals/${encodeURIComponent(approvalId)}`,
          bodyOptions("POST", note === undefined ? { status: decision } : { status: decision, note }, options),
        ),
      answerClarification: (runId, clarificationId, answers, options) =>
        http.request<ClarificationAnswerOutcome>(
          `/runs/${encodeURIComponent(String(runId))}/clarifications/${encodeURIComponent(clarificationId)}`,
          bodyOptions("POST", { answers }, options),
        ),
      streamTicket: (runId, options) =>
        http.request<{ ticket: string; expiresAt: string }>(
          `/runs/${encodeURIComponent(String(runId))}/stream-ticket`,
          bodyOptions("POST", {}, options),
        ),
    },

    agents: {
      list: (options) => http.request<{ agents: readonly Agent[] }>("/agents", optionsOf(options)),
      create: (input, options) => http.request<{ agent: Agent }>("/agents", bodyOptions("POST", input, options)),
      get: (agentId, options) => http.request<{ agent: Agent }>(`/agents/${encodeURIComponent(String(agentId))}`, optionsOf(options)),
      update: (agentId, input, options) =>
        http.request<{ agent: Agent }>(`/agents/${encodeURIComponent(String(agentId))}`, bodyOptions("PATCH", input, options)),
      share: (agentId, options) =>
        http.request<{ agent: Agent }>(`/agents/${encodeURIComponent(String(agentId))}/share`, bodyOptions("POST", {}, options)),
      unshare: (agentId, options) =>
        http.request<{ agent: Agent }>(`/agents/${encodeURIComponent(String(agentId))}/unshare`, bodyOptions("POST", {}, options)),
      delete: (agentId, options) =>
        http.request<{ agent: Agent; archived: boolean }>(`/agents/${encodeURIComponent(String(agentId))}`, methodOptions("DELETE", options)),
    },

    schedules: {
      list: (options) => http.request<{ schedules: readonly Schedule[] }>("/schedules", optionsOf(options)),
      create: (input, options) => http.request<{ schedule: Schedule }>("/schedules", bodyOptions("POST", input, options)),
      get: (scheduleId, options) => http.request<{ schedule: Schedule }>(`/schedules/${encodeURIComponent(String(scheduleId))}`, optionsOf(options)),
      update: (scheduleId, input, options) =>
        http.request<{ schedule: Schedule }>(`/schedules/${encodeURIComponent(String(scheduleId))}`, bodyOptions("PATCH", input, options)),
      trigger: (scheduleId, options) =>
        http.request<{ runId: RunId }>(`/schedules/${encodeURIComponent(String(scheduleId))}/trigger`, bodyOptions("POST", {}, options)),
      enable: async (scheduleId, options) => {
        await http.request<unknown>(`/schedules/${encodeURIComponent(String(scheduleId))}/enable`, methodOptions("POST", options));
      },
      disable: async (scheduleId, options) => {
        await http.request<unknown>(`/schedules/${encodeURIComponent(String(scheduleId))}/disable`, methodOptions("POST", options));
      },
      remove: async (scheduleId, options) => {
        await http.request<unknown>(`/schedules/${encodeURIComponent(String(scheduleId))}`, methodOptions("DELETE", options));
      },
    },

    memory: {
      list: async (options) => {
        const raw = await http.request<{ private?: readonly MemoryRecord[]; shared?: readonly MemoryRecord[] }>("/memory", optionsOf(options));
        return { private: raw.private ?? [], shared: raw.shared ?? [] };
      },
      create: (input, options) => http.request<{ memory: MemoryRecord }>("/memory", bodyOptions("POST", input, options)),
      confirm: (memoryId, input, options) => http.request<{ memory: MemoryRecord }>(`/memory/${encodeURIComponent(memoryId)}/confirm`, bodyOptions("POST", input, options)),
      promote: (memoryId, options) =>
        http.request<{ memory: MemoryRecord }>(`/memory/${encodeURIComponent(memoryId)}/promote`, bodyOptions("POST", {}, options)),
      remove: async (memoryId, options) => {
        await http.request<unknown>(`/memory/${encodeURIComponent(memoryId)}`, methodOptions("DELETE", options));
      },
    },

    files: {
      list: async (query) => {
        const raw = await http.request<{ files?: unknown[] }>("/files", { ...optionsOf(query), query: { prefix: query?.prefix } });
        return { files: (raw.files ?? []).map(normalizeFileSummary).filter((file): file is FileSummary => file !== null) };
      },
      upload: (input, options) => {
        if (!input.bytes.length || input.bytes.length > 32 * 1024 * 1024) throw new Error("File uploads must contain between 1 byte and 32 MiB.");
        let binary = "";
        for (let offset = 0; offset < input.bytes.length; offset += 16384) binary += String.fromCharCode(...input.bytes.subarray(offset, offset + 16384));
        return http.request("/files", bodyOptions("POST", { path: input.path, contentBase64: btoa(binary), contentType: input.contentType, ...(input.conversationId ? { conversationId: input.conversationId } : {}) }, options));
      },
      download: async (fileId, options) => {
        const file = await rawArrayBuffer(`/files/${encodeURIComponent(fileId)}`, options);
        return { fileId, mimeType: file.mimeType, filename: file.filename, bytes: file.bytes };
      },
    },

    credentials: {
      list: async (options) => {
        const raw = await http.request<Partial<CredentialList>>("/credentials", optionsOf(options));
        return { aiProviders: raw.aiProviders ?? [], databases: raw.databases ?? [], blobs: raw.blobs ?? [], secrets: raw.secrets ?? [] };
      },
      create: (input, options) => http.request<{ credential: Credential }>("/credentials", bodyOptions("POST", input, options)),
      test: async (credentialId, options) => {
        const raw = await http.request<unknown>(`/credentials/${encodeURIComponent(credentialId)}/test`, bodyOptions("POST", {}, options));
        return normalizeConnectionTest(raw);
      },
      revoke: async (credentialId, options) => {
        await http.request<unknown>(`/credentials/${encodeURIComponent(credentialId)}`, methodOptions("DELETE", options));
      },
    },

    usage: {
      get: (query) =>
        http.request<UsageRollup>("/usage", { ...optionsOf(query), query: { since: query?.since } }),
    },

    learning: {
      events: {
        list: (query) =>
          http.request<{ events: readonly LearningEvent[]; kind: string | null; status: string | null }>("/learning", {
            ...optionsOf(query),
            query: {
              ...(query?.limit === undefined ? {} : { limit: query.limit }),
              ...(query?.kind === undefined ? {} : { kind: query.kind }),
              ...(query?.status === undefined ? {} : { status: query.status }),
            },
          }),
      },
      refinements: {
        list: (query) =>
          http.request<{ refinements: readonly LearningRefinement[] }>("/learning/refinements", {
            ...optionsOf(query),
            query: query?.limit === undefined ? {} : { limit: query.limit },
          }),
        get: (refinementId, options) =>
          http.request<{ refinement: LearningRefinement }>(`/learning/refinements/${encodeURIComponent(refinementId)}`, optionsOf(options)),
        create: (input, options) =>
          http.request<{ refinement: LearningRefinement; queued: boolean; boundary: string }>(
            "/learning/refinements",
            bodyOptions("POST", {
              targetKind: input.targetKind,
              targetId: input.targetId,
              conversationId: input.conversationId,
              ...(input.branchId === undefined || input.branchId === null ? {} : { branchId: input.branchId }),
            }, options),
          ),
        apply: (refinementId, options) =>
          http.request<{ refinement: LearningRefinement; boundary: string }>(
            `/learning/refinements/${encodeURIComponent(refinementId)}/apply`,
            bodyOptions("POST", {}, options),
          ),
        revert: (refinementId, options) =>
          http.request<{ refinement: LearningRefinement }>(
            `/learning/refinements/${encodeURIComponent(refinementId)}/revert`,
            bodyOptions("POST", {}, options),
          ),
      },
    },

    snapshots: {
      list: (options) => http.request<{ snapshots: readonly WorkspaceSnapshotView[] }>("/snapshots", optionsOf(options)),
      create: (options) =>
        http.request<{ snapshot: WorkspaceSnapshotView; created: boolean }>("/snapshots", bodyOptions("POST", {}, options)),
      get: (snapshotId, options) =>
        http.request<{ snapshot: WorkspaceSnapshotView }>(`/snapshots/${encodeURIComponent(snapshotId)}`, optionsOf(options)),
      restore: (snapshotId, input, options) =>
        http.request<SnapshotRestoreResult>(`/snapshots/${encodeURIComponent(snapshotId)}/restore`, bodyOptions("POST", { confirm: input.confirm }, options)),
      remove: async (snapshotId, options) => {
        await http.request<unknown>(`/snapshots/${encodeURIComponent(snapshotId)}`, bodyOptions("DELETE", {}, options));
      },
    },

    automationTemplates: {
      list: (options) => http.request<{ templates: readonly AutomationTemplateView[] }>("/templates", optionsOf(options)),
      create: (input, options) =>
        http.request<{ template: AutomationTemplateView; created: boolean }>("/templates", bodyOptions("POST", {
          name: input.name,
          prompt: input.prompt,
          ...(input.cron === undefined ? {} : { cron: input.cron }),
          ...(input.runAt === undefined ? {} : { runAt: input.runAt }),
          ...(input.timezone === undefined ? {} : { timezone: input.timezone }),
          ...(input.parameters === undefined ? {} : { parameters: input.parameters }),
          ...(input.version === undefined ? {} : { version: input.version }),
        }, options)),
      get: (templateId, options) =>
        http.request<{ template: AutomationTemplateView }>(`/templates/${encodeURIComponent(templateId)}`, optionsOf(options)),
      use: (templateId, input, options) =>
        http.request<TemplateUseResult>(`/templates/${encodeURIComponent(templateId)}/use`, bodyOptions("POST", {
          agentId: input.agentId,
          ...(input.parameters === undefined ? {} : { parameters: input.parameters }),
          ...(input.title === undefined ? {} : { title: input.title }),
        }, options)),
      remove: async (templateId, options) => {
        await http.request<unknown>(`/templates/${encodeURIComponent(templateId)}`, bodyOptions("DELETE", {}, options));
      },
    },

    bundles: {
      list: (options) => http.request<{ bundles: readonly SkillBundleView[] }>("/bundles", optionsOf(options)),
      create: (input, options) =>
        http.request<{ bundle: SkillBundleView }>("/bundles", bodyOptions("POST", { name: input.name, skillNames: [...input.skillNames] }, options)),
      get: (bundleId, options) =>
        http.request<{ bundle: SkillBundleView; preview: BundleInstallPreview }>(`/bundles/${encodeURIComponent(bundleId)}`, optionsOf(options)),
      install: (bundleId, input, options) =>
        http.request<{ installed: true; bundleId: string; skills: number; preview: BundleInstallPreview }>(`/bundles/${encodeURIComponent(bundleId)}/install`, bodyOptions("POST", input, options)),
      remove: async (bundleId, options) => {
        await http.request<unknown>(`/bundles/${encodeURIComponent(bundleId)}`, bodyOptions("DELETE", {}, options));
      },
    },

    entitlements: {
      get: (options) => http.request<EntitlementsSummary>("/entitlements", optionsOf(options)),
    },

    audit: {
      list: async (options) => {
        const raw = await http.request<{ entries?: unknown[] }>("/audit", optionsOf(options));
        return { entries: (raw.entries ?? []).map(normalizeAuditEntry).filter((entry): entry is AuditEntry => entry !== null) };
      },
    },

    notifications: {
      list: async (options) => {
        const raw = await http.request<{ notifications?: unknown[] }>("/notifications", optionsOf(options));
        return {
          notifications: (raw.notifications ?? []).map(normalizeNotification).filter((entry): entry is NotificationRecord => entry !== null),
        };
      },
      markRead: (notificationId, options) =>
        http.request<{ marked: number }>(
          notificationId ? `/notifications/${encodeURIComponent(notificationId)}/read` : "/notifications",
          bodyOptions("POST", {}, options),
        ),
    },

    mcp: {
      list: (options) => http.request<{ servers: readonly McpServer[] }>("/mcp", optionsOf(options)),
      create: (input, options) => http.request<{ server: McpServer }>("/mcp", bodyOptions("POST", input, options)),
      get: (serverId, options) => http.request<{ server: McpServer }>(`/mcp/${encodeURIComponent(serverId)}`, optionsOf(options)),
      update: (serverId, input, options) =>
        http.request<{ server: McpServer }>(`/mcp/${encodeURIComponent(serverId)}`, bodyOptions("PATCH", input, options)),
      remove: async (serverId, options) => {
        await http.request<unknown>(`/mcp/${encodeURIComponent(serverId)}`, methodOptions("DELETE", options));
      },
      tools: (serverId, options) =>
        http.request<{ tools: readonly McpToolDescriptor[] }>(`/mcp/${encodeURIComponent(serverId)}/tools`, optionsOf(options)),
      test: (serverId, options) =>
        http.request<{ ok: boolean; tools: readonly McpToolDescriptor[] }>(`/mcp/${encodeURIComponent(serverId)}/test`, bodyOptions("POST", {}, options)),
    },

    tools: { list: (options) => http.request<WorkspaceToolCatalogue>("/tools", optionsOf(options)) },

    skills: {
      list: (query) => http.request<{ skills: readonly Skill[] }>("/skills", {
        ...optionsOf(query),
        query: query?.includeArchived === true ? { includeArchived: "true" } : {},
      }),
      create: (input, options) => http.request<{ skill: Skill }>("/skills", bodyOptions("POST", input, options)),
      get: (skillId, options) => http.request<{ skill: Skill }>(`/skills/${encodeURIComponent(skillId)}`, optionsOf(options)),
      update: (skillId, input, options) =>
        http.request<{ skill: Skill }>(`/skills/${encodeURIComponent(skillId)}`, bodyOptions("PATCH", input, options)),
      remove: (skillId, options) =>
        http.request<{ archived?: boolean; skill?: Skill }>(`/skills/${encodeURIComponent(skillId)}`, methodOptions("DELETE", options)),
      restore: (skillId, options) =>
        http.request<{ skill: Skill; restored: boolean }>(
          `/skills/${encodeURIComponent(skillId)}/restore`,
          bodyOptions("POST", {}, options),
        ),
      document: (skillId, options) =>
        http.request<{ document: string }>(`/skills/${encodeURIComponent(skillId)}/document`, optionsOf(options)),
      versions: (skillId, options) =>
        http.request<{ versions: readonly SkillVersionRecord[] }>(
          `/skills/${encodeURIComponent(skillId)}/versions`,
          optionsOf(options),
        ),
      share: (skillId, input, options) =>
        http.request<{ share: SkillShareView }>(`/skills/${encodeURIComponent(skillId)}/share`, bodyOptions("POST", input, options)),
      unshare: (skillId, input, options) =>
        http.request<{ revoked: boolean; shareId: string }>(`/skills/${encodeURIComponent(skillId)}/unshare`, bodyOptions("POST", input, options)),
    },

    skillShares: {
      targets: (options) => http.request<{ workspaces: readonly SkillShareTargetView[] }>("/skill-shares/targets", optionsOf(options)),
      owned: (options) => http.request<{ shares: readonly SkillShareView[] }>("/skill-shares/owned", optionsOf(options)),
      incoming: (options) => http.request<{ shares: readonly SkillShareView[] }>("/skill-shares", optionsOf(options)),
      accept: (shareId, input, options) => http.request(`/skill-shares/${encodeURIComponent(shareId)}/accept`, bodyOptions("POST", input, options)),
      update: (shareId, input, options) => http.request(`/skill-shares/${encodeURIComponent(shareId)}/update`, bodyOptions("POST", input, options)),
      revoke: (shareId, options) => http.request(`/skill-shares/${encodeURIComponent(shareId)}/revoke`, bodyOptions("POST", {}, options)),
    },

    curator: {
      overview: (options) => http.request<CuratorOverview>("/curator", optionsOf(options)),
      exportSkill: (skillId, options) => rawArrayBuffer(`/curator/skills/${encodeURIComponent(skillId)}/export`, options),
    },

    admin: {
      members: (options) => http.request<{ members: readonly Membership[] }>("/admin/members", optionsOf(options)),
      addMember: (input, options) =>
        http.request<{ membership: Membership }>("/admin/members", bodyOptions("POST", input, options)),
      removeMember: (userId, options) =>
        http.request<{ removed: boolean }>(`/admin/members/${encodeURIComponent(userId)}`, methodOptions("DELETE", options)),
      runRetention: (options) =>
        http.request<RetentionResult>("/admin/retention/run", bodyOptions("POST", {}, options)),
      sharedMemories: (options) =>
        http.request<{ memories: readonly MemoryRecord[] }>("/admin/shared-memories", optionsOf(options)),
      removeSharedMemory: (memoryId, options) =>
        http.request<{ deleted: boolean }>(`/admin/shared-memories/${encodeURIComponent(memoryId)}`, methodOptions("DELETE", options)),
      toolPolicies: (options) =>
        http.request<{ policies: readonly AdminToolPolicy[] }>("/admin/tool-policies", optionsOf(options)),
      setToolPolicy: (input, options) =>
        http.request<{ policy: AdminToolPolicy }>("/admin/tool-policies", bodyOptions("PUT", input, options)),
      removeToolPolicy: (toolId, options) =>
        http.request<{ removed: boolean }>(`/admin/tool-policies/${encodeURIComponent(toolId)}`, methodOptions("DELETE", options)),
    },

    storage: {
      getRoute: (options) => http.request<StorageRouteResponse>("/storage/route", optionsOf(options)),
      setRoute: (input, options) =>
        http.request<{ ok: boolean; route: StorageRouteResponse }>("/storage/route", bodyOptions("PUT", input, options)),
      startMigration: (input, options) =>
        http.request<{ ok: boolean; job: StorageMigrationJobResponse }>(
          "/storage/migrate",
          bodyOptions("POST", input, options),
        ),
      getMigrationJob: (jobId, options) =>
        http.request<{ job: StorageMigrationJobResponse }>(
          `/storage/migrate/${encodeURIComponent(jobId)}`,
          optionsOf(options),
        ),
    },
    billing: {
      portal: options => http.request("/billing/portal", methodOptions("POST", options)),
      cancel: options => http.request("/billing/cancel", methodOptions("POST", options)),
      preview: (plan, options) => http.request("/billing/preview", bodyOptions("POST", { plan }, options)),
      change: (intentId, options) => http.request("/billing/change", bodyOptions("POST", { intentId }, options)),
      history: options => http.request("/billing/history", optionsOf(options)),
      export: async options => (await http.requestRaw("/billing/export", optionsOf(options))).arrayBuffer(),
      abandon: options => http.request("/billing/checkout/abandon", methodOptions("POST", options)),
      deleteAccount: options => http.request("/account", methodOptions("DELETE", options)),
      get: (options) => http.request<BillingOverview>("/billing", optionsOf(options)),
      checkout: (input, options) =>
        http.request<BillingCheckoutResult>("/billing/checkout", bodyOptions("POST", input, options)),
      verifyPlayPurchase: (input, options) =>
        http.request<PlayPurchaseVerificationResult>("/billing/play", bodyOptions("POST", input, options)),
    },
    worktrees: {
      list: (conversationId, query) =>
        http.request<{ worktrees: readonly WorktreeState[] }>(`/worktrees`, {
          ...optionsOf(query),
          query: {
            conversationId: String(conversationId),
            ...(query?.branchId === undefined ? {} : { branchId: query.branchId }),
          },
        }),
    },
    providerIdentities: {
      status: (provider, options) =>
        http.request<ProviderIdentityStatusResponse>(`/provider-identity?provider=${encodeURIComponent(provider)}`, optionsOf(options)),
    },
    home: {
      get: (options) => http.request<HomeResolution>("/home", optionsOf(options)),
      set: (defaultWorkspaceId, options) =>
        http.request<HomeResolution>("/home", bodyOptions("PUT", { defaultWorkspaceId }, options)),
    },
    projectInits: {
      directories: (conversationId, branchId, options) => http.request<{ directories: readonly string[]; filesSelected: number }>("/project-inits/directories", { ...optionsOf(options), query: { conversationId, ...(branchId ? { branchId } : {}) } }),
      list: (options) => http.request<{ jobs: readonly ProjectInitItem[] }>("/project-inits", optionsOf(options)),
      create: (input, options) => http.request<{ job: ProjectInitItem; created: boolean }>("/project-inits", bodyOptions("POST", input, options)),
      get: (jobId, options) => http.request<{ job: ProjectInitItem }>(`/project-inits/${encodeURIComponent(jobId)}`, optionsOf(options)),
      apply: (jobId, input, options) => http.request<{ job: ProjectInitItem; fileId: string; version: number }>(`/project-inits/${encodeURIComponent(jobId)}/apply`, bodyOptions("POST", input, options)),
      discard: (jobId, options) => http.request<{ discarded: boolean }>(`/project-inits/${encodeURIComponent(jobId)}/discard`, bodyOptions("POST", {}, options)),
    },
    devices: {
      list: (options) => http.request<{ devices: readonly DeviceItem[] }>("/devices", optionsOf(options)),
      register: (input, options) => http.request<{ device: DeviceItem }>("/devices", bodyOptions("POST", input, options)),
      setPushToken: (deviceId, token, options) => http.request<{ device: DeviceItem }>(`/devices/${encodeURIComponent(deviceId)}/push`, bodyOptions("PATCH", { token }, options)),
      clearPushToken: (deviceId, options) => http.request<{ device: DeviceItem }>(`/devices/${encodeURIComponent(deviceId)}/push`, methodOptions("DELETE", options)),
      revoke: (deviceId, options) =>
        http.request<{ revoked: boolean }>(`/devices/${encodeURIComponent(deviceId)}`, methodOptions("DELETE", options)),
    },
    inputRequests: {
      list: (options) => http.request<{ now: string; requests: readonly InputRequest[] }>("/input-requests", optionsOf(options)),
      answer: (runId, requestId, answers, options) =>
        http.request<{ ok: boolean; clarificationId: string; sequence: number }>(`/runs/${encodeURIComponent(runId)}/clarifications/${encodeURIComponent(requestId)}`, bodyOptions("POST", { answers }, options)),
      answerForm: (runId, requestId, submission, options) =>
        http.request<{ ok: boolean; clarificationId: string; sequence: number }>(`/runs/${encodeURIComponent(runId)}/clarifications/${encodeURIComponent(requestId)}`, bodyOptions("POST", {
          answers: submission.answers,
          ...(submission.protectedAnswers === undefined || submission.protectedAnswers.length === 0 ? {} : { protectedAnswers: submission.protectedAnswers }),
          ...(submission.ordinaryFieldIds === undefined || submission.ordinaryFieldIds.length === 0 ? {} : { ordinaryFieldIds: submission.ordinaryFieldIds }),
          ...(submission.formRevision === undefined ? {} : { formRevision: submission.formRevision }),
        }, options)),
      decline: (runId, requestId, options) =>
        http.request<{ ok: boolean; declined: boolean; clarificationId: string; sequence: number }>(`/runs/${encodeURIComponent(runId)}/clarifications/${encodeURIComponent(requestId)}`, bodyOptions("POST", { declined: true }, options)),
    },
    yolo: {
      get: (options) => http.request<{ mode: YoloModeState }>("/yolo", optionsOf(options)),
      update: (input, options) => http.request<{ mode: YoloModeState }>("/yolo", bodyOptions("PUT", input, options)),
    },
    boards: {
      list: (query) => http.request<{ boards: readonly BoardRecord[] }>("/boards", {
        ...optionsOf(query),
        query: query?.includeArchived === true ? { includeArchived: "true" } : {},
      }),
      summary: (options) => http.request<{ summary: string }>("/boards/summary", optionsOf(options)),
      create: (input, options) => http.request<{ board: BoardRecord }>("/boards", bodyOptions("POST", input, options)),
      get: (boardId, query) => http.request<{ view: BoardView }>(`/boards/${encodeURIComponent(String(boardId))}`, {
        ...optionsOf(query),
        query: query?.includeArchived === true ? { includeArchived: "true" } : {},
      }),
      update: (boardId, input, options) => http.request<{ board: BoardRecord }>(`/boards/${encodeURIComponent(String(boardId))}`, bodyOptions("PATCH", input, options)),
      archive: (boardId, options) => http.request<{ board: BoardRecord }>(`/boards/${encodeURIComponent(String(boardId))}/archive`, bodyOptions("POST", {}, options)),
      restore: (boardId, options) => http.request<{ board: BoardRecord }>(`/boards/${encodeURIComponent(String(boardId))}/restore`, bodyOptions("POST", {}, options)),
      addColumn: (boardId, input, options) => http.request<{ column: BoardColumn }>(`/boards/${encodeURIComponent(String(boardId))}/columns`, bodyOptions("POST", input, options)),
      updateColumn: (boardId, columnId, input, options) => http.request<{ column: BoardColumn }>(`/boards/${encodeURIComponent(String(boardId))}/columns/${encodeURIComponent(String(columnId))}`, bodyOptions("PATCH", input, options)),
      removeColumn: (boardId, columnId, options) => http.request<{ removed: boolean }>(`/boards/${encodeURIComponent(String(boardId))}/columns/${encodeURIComponent(String(columnId))}`, methodOptions("DELETE", options)),
      addTask: (boardId, input, options) => http.request<{ task: BoardTask }>(`/boards/${encodeURIComponent(String(boardId))}/tasks`, bodyOptions("POST", input, options)),
      updateTask: (boardId, taskId, input, options) => http.request<{ task: BoardTask }>(`/boards/${encodeURIComponent(String(boardId))}/tasks/${encodeURIComponent(String(taskId))}`, bodyOptions("PATCH", input, options)),
      moveTask: (boardId, taskId, input, options) => http.request<{ task: BoardTask }>(`/boards/${encodeURIComponent(String(boardId))}/tasks/${encodeURIComponent(String(taskId))}/move`, bodyOptions("POST", input, options)),
      addDependency: (boardId, taskId, dependsOnTaskId, options) => http.request<{ dependency: { taskId: string; dependsOnTaskId: string } }>(
        `/boards/${encodeURIComponent(String(boardId))}/tasks/${encodeURIComponent(String(taskId))}/dependencies`,
        bodyOptions("POST", { dependsOnTaskId }, options),
      ),
      removeDependency: (boardId, taskId, dependsOnTaskId, options) => http.request<{ removed: boolean }>(
        `/boards/${encodeURIComponent(String(boardId))}/tasks/${encodeURIComponent(String(taskId))}/dependencies/${encodeURIComponent(String(dependsOnTaskId))}`,
        methodOptions("DELETE", options),
      ),
      messages: (boardId, taskId, options) => http.request<{ messages: readonly BoardMessage[] }>(
        `/boards/${encodeURIComponent(String(boardId))}/tasks/${encodeURIComponent(String(taskId))}/messages`, optionsOf(options),
      ),
      inbox: (boardId, taskId, options) => http.request<{ messages: readonly BoardMessage[] }>(
        `/boards/${encodeURIComponent(String(boardId))}/tasks/${encodeURIComponent(String(taskId))}/inbox`, optionsOf(options),
      ),
      postMessage: (boardId, taskId, input, options) => http.request<{ message: BoardMessage }>(
        `/boards/${encodeURIComponent(String(boardId))}/tasks/${encodeURIComponent(String(taskId))}/messages`, bodyOptions("POST", input, options),
      ),
      files: (boardId, taskId, options) => http.request<{ files: readonly BoardTaskFile[] }>(
        `/boards/${encodeURIComponent(String(boardId))}/tasks/${encodeURIComponent(String(taskId))}/files`, optionsOf(options),
      ),
      copyFile: (boardId, taskId, input, options) => http.request<{ artifact: BoardArtifact }>(
        `/boards/${encodeURIComponent(String(boardId))}/tasks/${encodeURIComponent(String(taskId))}/files`, bodyOptions("POST", input, options),
      ),
      removeFile: (boardId, taskId, artifactId, options) => http.request<{ removed: boolean }>(
        `/boards/${encodeURIComponent(String(boardId))}/tasks/${encodeURIComponent(String(taskId))}/files/${encodeURIComponent(String(artifactId))}`,
        methodOptions("DELETE", options),
      ),
      publishFile: (boardId, taskId, artifactId, input, options) => http.request<{ path: string; version: number; sourcePath: string }>(
        `/boards/${encodeURIComponent(String(boardId))}/tasks/${encodeURIComponent(String(taskId))}/files/${encodeURIComponent(String(artifactId))}/publish`,
        bodyOptions("POST", input, options),
      ),
      artifacts: (boardId, taskId, options) => http.request<{ artifacts: readonly BoardArtifact[] }>(
        `/boards/${encodeURIComponent(String(boardId))}/tasks/${encodeURIComponent(String(taskId))}/artifacts`, optionsOf(options),
      ),
      linkArtifact: (boardId, taskId, input, options) => http.request<{ artifact: BoardArtifact }>(
        `/boards/${encodeURIComponent(String(boardId))}/tasks/${encodeURIComponent(String(taskId))}/artifacts`, bodyOptions("POST", input, options),
      ),
      removeArtifact: (boardId, taskId, artifactId, options) => http.request<{ removed: boolean }>(
        `/boards/${encodeURIComponent(String(boardId))}/tasks/${encodeURIComponent(String(taskId))}/artifacts/${encodeURIComponent(String(artifactId))}`,
        methodOptions("DELETE", options),
      ),
      dispatch: (boardId, taskId, input, options) => http.request<{ dispatch: BoardDispatch; runId: string | null; branchId: string | null }>(
        `/boards/${encodeURIComponent(String(boardId))}/tasks/${encodeURIComponent(String(taskId))}/dispatch`, bodyOptions("POST", input ?? {}, options),
      ),
      cancelTask: (boardId, taskId, input, options) => http.request<{ cancelled: boolean }>(
        `/boards/${encodeURIComponent(String(boardId))}/tasks/${encodeURIComponent(String(taskId))}/cancel`, bodyOptions("POST", input ?? {}, options),
      ),
      settleDispatches: (boardId, options) => http.request<{ settled: number; failed: number; cancelled: number }>(
        `/boards/${encodeURIComponent(String(boardId))}/dispatches/settle`, bodyOptions("POST", {}, options),
      ),
    },
    calls: {
      list: (options) => http.request<{ sessions: readonly CallSessionView[] }>("/calls", optionsOf(options)),
      get: (sessionId, options) => http.request<{ session: CallSessionView }>(`/calls/${encodeURIComponent(sessionId)}`, optionsOf(options)),
      ring: (input, options) => http.request<{ session: CallSessionView; created: boolean }>("/calls/ring", bodyOptions("POST", input, options)),
      answer: (sessionId, input, options) => http.request<CallAnswerResponse>(`/calls/${encodeURIComponent(sessionId)}/answer`, bodyOptions("POST", input, options)),
      decline: (sessionId, input, options) => http.request<{ session: CallSessionView }>(`/calls/${encodeURIComponent(sessionId)}/decline`, bodyOptions("POST", input, options)),
      hangup: (sessionId, options) => http.request<{ session: CallSessionView }>(`/calls/${encodeURIComponent(sessionId)}/hangup`, bodyOptions("POST", {}, options)),
    },
    callPreferences: {
      get: (options) => http.request<{ scopeKey: string; preferences: CallPreferenceSettings }>("/call-preferences", optionsOf(options)),
      update: (input, options) => http.request<{ scopeKey: string; preferences: CallPreferenceSettings }>("/call-preferences", bodyOptions("PUT", input, options)),
      reset: (options) => http.request<{ scopeKey: string; preferences: CallPreferenceSettings }>("/call-preferences", methodOptions("DELETE", options)),
    },
    presence: {
      list: (options) => http.request<{ now: string; leases: readonly PresenceLease[] }>("/presence", optionsOf(options)),
      update: (input, options) => http.request<{ accepted: boolean; observedAt: string; expiresAt: string }>("/presence", bodyOptions("POST", input, options)),
    },
  };
}

/**
 * `Content-Disposition` carries the download name. Both the plain and the
 * RFC 5987 `filename*` forms are handled, and a missing name falls back to a
 * stable placeholder rather than `undefined`.
 */
export function filenameFromDisposition(disposition: string): string {
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (encoded?.[1]) {
    try {
      return decodeURIComponent(encoded[1].trim());
    } catch {
      return encoded[1].trim();
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  return plain?.[1]?.trim() ?? "dayvise-file";
}

export type { SubagentDelegationSummary };
